'use client';

import { NotificationAnchorBadgeSlot, NotificationAnchorContentSlot } from '@magicschool/spellbook/components/NotificationAnchor/slots';
import { useId } from '@magicschool/spellbook/hooks/useId';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva } from 'class-variance-authority';
import type { Slot as RadixSlot } from 'radix-ui/slot';
import type { ComponentProps } from 'react';

export type NotificationAnchorProps = ComponentProps<typeof RadixSlot> & {
  /** Shows the badge. The wrapper stays mounted either way. @default true */
  show?: boolean;
  /** What the badge means, e.g. "3 unread". While `show` is true it is screen reader text beside the element, which also points at it through `aria-describedby`. */
  description?: string;
};

export const notificationAnchorVariants = cva(['relative', 'inline-flex', 'max-w-full']);

/**
 * Pins a badge to the top-right corner (top-left in RTL) of the single element it wraps, centered on the corner and outside the element so a clipping parent such as `Button` can't cut it off.
 *
 * ### When to use
 * - Marking a button, icon button, avatar, input, or badge as unread, new, or needing attention
 * - Showing a count on the corner of a control
 * - A count or a description on an `IconButton`, which its built-in `<IconButton.NotificationBadge>` dot doesn't provide
 *
 * ### When NOT to use
 * - For a badge inline with text or inside a row: render `NotificationBadge` directly
 *
 * ### API
 * - `show`: Toggles the badge, `true` by default. The wrapper stays mounted, so the wrapped element keeps its focus and state.
 * - `description`: What the badge means to a screen reader, e.g. "3 unread".
 * - `className`: Styles the wrapper. Pass `w-full` when the wrapped element is full width, such as `TextInput`.
 * - Every other prop, and `ref`, is forwarded to the wrapped element, so the anchor works as the `asChild` child of `Tooltip.Trigger` and `DropdownMenu.Trigger`. A `className` from such a trigger styles the wrapper too, so state classes like `data-[state=open]:` belong on the wrapped element.
 *
 * ### Composition (slots)
 * - `<NotificationAnchor.Badge>`: The corner badge. Rendered by default as an `xxs` `NotificationBadge`; include it only to change it. Accepts the `NotificationBadge` props that affect how it looks (`size`, `variant`, `pulse`, and children as the count); its meaning comes from `description`. With `overrideDefault`, renders your own content in the same corner. Either way it keeps the corner placement, `aria-hidden`, and forced-colors handling, and merges your `className`, which also moves it, e.g. `top-1 end-1` to sit on a round `Avatar` rather than its bounding box.
 * - `<NotificationAnchor.Content>`: Required. Wraps the single element the badge is pinned to. It takes no props of its own; put them on the element, where they combine with the props and `ref` the anchor forwards.
 *
 * ### Visual states
 * - **Dot**: The default `xxs` badge, a small circle with a ring that separates it from the element beneath.
 * - **Count**: Badge sizes `xs` to `md` show their children; long counts grow into a pill and truncate past the badge's maximum width.
 * - **Hidden**: `show={false}` removes the badge and its description; the wrapped element is unchanged.
 *
 * ### Accessibility
 * - The badge is hidden from assistive technology; `description` carries its meaning, and is dropped while `show` is false
 * - `description` is visually hidden text beside the element, so it is read in reading order on any element, and the element points at it through `aria-describedby`, merged with any ids already there (such as a tooltip's), so it is read on focus too
 * - Without `description`, put the state in the wrapped element's accessible name
 * - The badge stays visible in forced-colors (Windows High Contrast) mode, where background colors are otherwise removed
 */
const NotificationAnchorBase: React.FC<NotificationAnchorProps> = ({ show = true, description, className, children, ...props }) => {
  const descriptionId = useId();
  const shownDescription = show ? description : undefined;
  const contentProps = { anchorProps: props, anchorDescriptionId: shownDescription ? descriptionId : undefined };
  const { slots } = useSlots(children, {
    badge: { Component: NotificationAnchorBadgeSlot },
    content: { Component: NotificationAnchorContentSlot, props: contentProps },
  });

  if (!slots.content) console.warn('NotificationAnchor requires a Content slot.');

  return (
    <span data-spellbook-name="notification-anchor" className={cn(notificationAnchorVariants(), className)}>
      {slots.content}
      {show ? slots.badge : null}
      {shownDescription ? (
        <span id={descriptionId} className="sr-only">
          {shownDescription}
        </span>
      ) : null}
    </span>
  );
};

export const NotificationAnchor = Object.assign(NotificationAnchorBase, {
  Badge: NotificationAnchorBadgeSlot,
  Content: NotificationAnchorContentSlot,
});
